import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, money, fmtDate, fmtDateTime, ago, todayStr, waLink, mapsLink, timeLeft, productName } from "./api";
import { Icon, Badge, Empty, useToast } from "./Ui.jsx";
import PushToggle from "./PushToggle.jsx";
import { useAutoRefresh } from "./useAutoRefresh";

// Valores por defecto mientras carga /api/me (los reales vienen del servidor)
const DEFAULT_ME = {
  warnings: 0, max_warnings: 5, owed: 0, delivered_count: 0, cycle_start: null, due_date: null,
  commission_fixed: 30, compost_commission: 30, bulk_percent: 10, delivery_hours: 32, cycle_days: 16,
  card_number: "", card_bank: "", warning_list: [],
};

/* ---------- Tarjeta de pedido grande: el socio elige cuántos litros aporta ---------- */
function BulkCard({ o, pct, onSave }) {
  const total = Number(o.liters);
  const covered = Number(o.covered_liters);
  const mine = Number(o.my_liters);
  const room = Math.max(total - covered, 0) + mine; // lo máximo que este socio puede poner
  const [val, setVal] = useState(mine || "");
  const perLiter = Number(o.payout_per_liter) || 0;
  const typed = Math.min(Math.max(Number(val) || 0, 0), room);
  const percent = Math.min(100, Math.round((covered / total) * 100));
  const full = room === 0;

  return (
    <article className="o-card">
      <div className="o-top">
        <b>Pedido grande · {total} L</b>
        <span className="o-price">{money(perLiter)} / L</span>
      </div>

      <div className="progress"><i style={{ width: `${percent}%` }} /></div>
      <div className="muted small">
        {covered} de {total} L cubiertos · faltan {Math.max(total - covered, 0)} L
      </div>

      <div className="alert alert-info">
        {o.pickup_date ? `El ${fmtDate(o.pickup_date)}` : "El día que te avisemos"}, un día antes de la entrega al cliente,
        pasará un recolector por tu producto. Se te pagará el precio menos {pct}% de comisión.
      </div>

      {full ? (
        <div className="muted small">Este pedido ya está cubierto.</div>
      ) : (
        <>
          <div className="inline-form">
            <input
              type="number" min="0" max={room} inputMode="numeric"
              placeholder={`¿Cuántos litros aportas? (máx. ${room})`}
              value={val} onChange={(e) => setVal(e.target.value)}
            />
            <button className="btn" disabled={Number(val) > room || (!mine && !Number(val))} onClick={() => onSave(o.id, Number(val) || 0)}>
              {mine ? "Actualizar" : "Aportar"}
            </button>
          </div>
          <div className="muted small">
            {typed > 0 ? `Recibirías ${money(typed * perLiter)} por ${typed} L.` : "Escribe 0 para quitar tu aportación."}
            {mine > 0 && <> Ahora aportas {mine} L ({money(mine * perLiter)}).</>}
          </div>
        </>
      )}
    </article>
  );
}
/* ---------- Dónde pagar (solo texto, sin pasarela) ---------- */
function PayCard({ notify, number, bank }) {
  if (!number) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(number.replace(/\s/g, ""));
      notify("Número de tarjeta copiado");
    } catch {
      notify("No se pudo copiar. Mantén presionado el número para copiarlo.", true);
    }
  };
  return (
    <article className="o-card">
      <div className="o-top">
        <b>Dónde pagar tu comisión</b>
        <span className="tag">{bank}</span>
      </div>
      <div style={{ userSelect: "all", WebkitUserSelect: "all", fontSize: "1.4rem", fontWeight: 700, letterSpacing: ".08em" }}>
        {number}
      </div>
      <div className="muted small">Tarjeta {bank} · depósito o transferencia. Después de pagar avisa al administrador.</div>
      <button className="btn btn-block" onClick={copy}>Copiar número</button>
    </article>
  );
}
export default function ProviderDashboard({ user, onLogout }) {
  const [tab, setTab] = useState("available");
  const [me, setMe] = useState(null);
  const [available, setAvailable] = useState([]);
  const [mine, setMine] = useState([]);
  const [bulkMine, setBulkMine] = useState([]);
  const [payments, setPayments] = useState([]);
  const [toast, notify] = useToast();

    const seq = useRef(0);

  const loadAll = useCallback(async () => {
    const id = ++seq.current;
    try {
      const [m, a, o, b, p] = await Promise.all([
        api("/api/me"),
        api("/api/orders/available"),
        api("/api/orders/mine"),
        api("/api/bulk/mine"),
        api("/api/payments/mine"),
      ]);
      if (id !== seq.current) return; // llegó una carga más nueva: esta ya está vieja
      setMe(m); setAvailable(a); setMine(o); setBulkMine(b); setPayments(p);
    } catch (e) { notify(e.message, true); }
  }, [notify]);

   useAutoRefresh(loadAll);

  const r = me || DEFAULT_ME;

     const claim = async (id) => {
    if (!window.confirm(`Al tomarlo tendrás ${r.delivery_hours} horas para entregarlo. Si no, se marcará como entregado solo y recibirás una advertencia. ¿Lo tomas?`)) return;
    try {
      const taken = await api("/api/orders/claim", { method: "POST", body: { order_id: id } });
      seq.current++; // descarta las cargas que empezaron antes de reclamar
      setAvailable((list) => list.filter((o) => o.id !== id));
      setMine((list) => [taken, ...list.filter((o) => o.id !== taken.id)]);
      notify("Pedido tuyo. Ya puedes ver los datos del cliente.");
      setTab("mine");
      loadAll(); // carga fresca, ya con el pedido guardado en el servidor
    } catch (e) {
      notify(e.message, true);
      setAvailable((list) => list.filter((o) => o.id !== id));
    }
  };

  const deliver = async (id) => {
    if (!window.confirm("¿Ya entregaste este pedido?")) return;
    try {
      await api(`/api/orders/${id}/delivered`, { method: "PATCH" });
      notify("Entrega registrada");
    } catch (e) { notify(e.message, true); }
    loadAll();
  };

  const contribute = async (id, liters) => {
    try {
      await api(`/api/orders/${id}/contribute`, { method: "POST", body: { liters } });
      notify(liters > 0 ? "Aportación guardada" : "Aportación quitada");
    } catch (e) { notify(e.message, true); }
    loadAll();
  };

  const normal = available.filter((o) => Number(o.is_bulk) !== 1);
  const bulk = available.filter((o) => Number(o.is_bulk) === 1);
  const active = mine.filter((o) => o.status === "tomado");
  const history = mine.filter((o) => o.status !== "tomado");
  const late = r.due_date && r.due_date < todayStr();
  const cycleBadge = r.owed > 0 ? (late ? "vencido" : "pendiente") : "pagado";

  return (
    <div className="cz prov">
      <header className="p-head">
        <div>
          <div className={`warn-pill ${r.warnings >= r.max_warnings - 1 && r.warnings > 0 ? "hot" : ""}`}>
            <Icon name="alert" size={14} /> Advertencias {r.warnings}/{r.max_warnings}
          </div>
          <div className="brand"><span className="brand-dot" />Cozobi</div>
          <div className="muted small">Hola, {user.name}</div>
        </div>
        <button className="icon-btn" onClick={onLogout} aria-label="Salir"><Icon name="logout" size={20} /></button>
      </header>

      <main className="p-body">
        <section className={`hero ${late && r.owed > 0 ? "hero-bad" : ""}`}>
          <span>Comisión por pagar</span>
          <b>{money(r.owed)}</b>
          <small>
            {r.owed === 0
              ? "Estás al corriente"
              : late
                ? `Vencida desde el ${fmtDate(r.due_date)}`
                : `Pago límite: ${fmtDate(r.due_date)}`}
          </small>
        </section>

        <PushToggle notify={notify} />

        {/* DISPONIBLES */}
        {tab === "available" && (
          <>
            {bulk.length > 0 && <h2 className="section-title">Pedidos grandes</h2>}
            {bulk.map((o) => <BulkCard key={o.id} o={o} pct={r.bulk_percent} onSave={contribute} />)}

            <h2 className="section-title">Pedidos disponibles</h2>
            {normal.map((o) => (
              <article className="o-card" key={o.id}>
                <div className="o-top">
                    <b>{o.quantity} × {productName(o)}</b>
                  <span className="o-price">{money(o.total_price)}</span>
                </div>
                <div className="o-line"><Icon name="pin" /><span>{o.address}</span></div>
                <div className="muted small">
                  Comisión fija {money(o.commission_amount)} · {r.delivery_hours} h para entregar · {ago(o.created_at)}
                </div>
                <button className="btn btn-block" onClick={() => claim(o.id)}>Agarrar pedido</button>
              </article>
            ))}
            {normal.length === 0 && <Empty>No hay pedidos por ahora. Se actualiza solo.</Empty>}
          </>
        )}

        {/* MIS ENTREGAS */}
        {tab === "mine" && (
          <>
            <h2 className="section-title">Por entregar</h2>
            {active.map((o) => {
              const left = timeLeft(o.due_at);
              return (
                <article className="o-card" key={o.id}>
                  <div className="o-top">
                      <b>{o.quantity} × {productName(o)}</b>
                    <span className="o-price">{money(o.total_price)}</span>
                  </div>
                  <div><b>{o.customer_name || "Cliente"}</b><div className="muted small">Cobrar {money(o.total_price)} al entregar</div></div>
                  <div className="o-line"><Icon name="pin" /><span>{o.address}</span></div>
                  <div className={left === "vencido" ? "alert alert-bad" : "muted small"}>
                    {left === "vencido"
                      ? "El plazo venció: el pedido se cerrará solo y recibirás una advertencia."
                      : `Entrégalo antes del ${fmtDateTime(o.due_at)} (te quedan ${left}).`}
                  </div>
                  <div className="o-actions">
                    <a className="btn btn-sm btn-soft" href={`tel:+52${o.customer_phone}`}><Icon name="phone" size={16} /> Llamar</a>
                    <a className="btn btn-sm btn-soft" href={waLink(o.customer_phone, "Hola, soy de Cozobi. Voy en camino con tu pedido.")} target="_blank" rel="noreferrer">WhatsApp</a>
                    <a className="btn btn-sm btn-soft" href={mapsLink(o.address)} target="_blank" rel="noreferrer">Mapa</a>
                  </div>
                  <button className="btn btn-block" onClick={() => deliver(o.id)}><Icon name="check" /> Marcar entregado</button>
                </article>
              );
            })}
            {active.length === 0 && <Empty>No tienes entregas pendientes.</Empty>}

            {bulkMine.length > 0 && (
              <>
                <h2 className="section-title">Mis aportaciones (pedidos grandes)</h2>
                <div className="card flush">
                  {bulkMine.map((b) => (
                    <div className="list-row" key={b.id}>
                      <div>
                        <b>{b.liters} L de {b.order_liters} L</b>
                        <div className="muted small">Recolecta: {b.pickup_date ? fmtDate(b.pickup_date) : "por avisar"}</div>
                      </div>
                      <div className="right">
                        <b>{money(b.payout_amount)}</b>
                        <div><Badge s={b.paid_at ? "pagado" : "pendiente"} /></div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            <h2 className="section-title">Historial</h2>
            <div className="card flush">
              {history.map((o) => (
                <div className="list-row" key={o.id}>
                  <div>
                    <b>{o.customer_name || "Cliente"}</b>
                    <div className="muted small">
                      {o.quantity} × {productName(o)} · {fmtDateTime(o.delivered_at)}
                      {Number(o.auto_delivered) === 1 && <span className="tag">cerrado por plazo</span>}
                    </div>
                  </div>
                  <div className="right"><b>{money(o.total_price)}</b><div><Badge s={o.status} /></div></div>
                </div>
              ))}
              {history.length === 0 && <Empty>Aún no tienes entregas.</Empty>}
            </div>
          </>
        )}

        {/* PAGOS */}
        {tab === "payments" && (
          <>
            <h2 className="section-title">Mi ciclo de pago</h2>
                        <PayCard notify={notify} number={r.card_number} bank={r.card_bank} />
            <div className="alert alert-info">
              Pagas una comisión fija por pedido entregado: {money(r.commission_fixed)} por bidón y {money(r.compost_commission)} por composta. Cada {r.cycle_days} días debes pagar lo acumulado;
              si no, tu acceso se suspende hasta que te pongas al corriente.
            </div>
            <article className="o-card">
              <div className="o-top">
                <b>{fmtDate(r.cycle_start)} – {fmtDate(r.due_date)}</b>
                <Badge s={cycleBadge} />
              </div>
              <div className="kv"><span>Pedidos entregados</span><b>{r.delivered_count}</b></div>
              <div className="kv"><span>Comisión a pagar</span><b>{money(r.owed)}</b></div>
              {r.owed > 0 && <div className="muted small">Pago límite: {fmtDate(r.due_date)}</div>}
            </article>

            <h2 className="section-title">Pagos anteriores</h2>
            <div className="card flush">
              {payments.map((p) => (
                <div className="list-row" key={p.id}>
                  <div>
                    <b>{fmtDate(p.cycle_start)} – {fmtDate(p.paid_at)}</b>
                    <div className="muted small">{p.orders_count} entregas</div>
                  </div>
                  <div className="right"><b>{money(p.commission_paid)}</b><div><Badge s="pagado" /></div></div>
                </div>
              ))}
              {payments.length === 0 && <Empty>Aún no hay pagos registrados.</Empty>}
            </div>

            <h2 className="section-title">Advertencias ({r.warnings}/{r.max_warnings})</h2>
            <div className="alert alert-info">
              Recibes una advertencia si no entregas un pedido en {r.delivery_hours} horas. Con {r.max_warnings} se elimina tu cuenta.
            </div>
            <div className="card flush">
              {r.warning_list.map((w, i) => (
                <div className="list-row" key={i}>
                  <div><b>{w.reason}</b></div>
                  <div className="right muted small">{fmtDateTime(w.created_at)}</div>
                </div>
              ))}
              {r.warning_list.length === 0 && <Empty>No tienes advertencias.</Empty>}
            </div>
          </>
        )}
      </main>

      <nav className="p-nav">
        <button className={tab === "available" ? "on" : ""} onClick={() => setTab("available")}>
          <Icon name="orders" size={22} /><span>Pedidos{available.length > 0 && <i className="dot">{available.length}</i>}</span>
        </button>
        <button className={tab === "mine" ? "on" : ""} onClick={() => setTab("mine")}>
          <Icon name="box" size={22} /><span>Entregas{active.length > 0 && <i className="dot">{active.length}</i>}</span>
        </button>
        <button className={tab === "payments" ? "on" : ""} onClick={() => setTab("payments")}>
          <Icon name="wallet" size={22} /><span>Pagos</span>
        </button>
      </nav>
      {toast}
    </div>
  );
}